import { I18n } from '../../helpers/i18n.mjs';
import {
	COMPLEX_INPUT_TYPES,
	getAIInput,
	hasUnsupportedFieldInstructions,
	sanitizeInputConfigArray,
	sanitizePathForVariables,
	CUSTOM_MAPPABLE_INPUT_TYPES,
	SUPPORTED_INPUT_TYPES,
} from './let-ai-decide.utils';
import classes from './let-ai-decide.module.scss';
import { AIInputConfig, FieldLike, FormanConfigLike, FormanLike, GetCustomAIInputConfig } from './let-ai-decide.types';
import Features from '../../features.mjs';
import EventManager from '../../eventManager.mjs';
import EVENTS from '../../events.mjs';
import { Coder } from '../../controls/coder.mjs';

export type LetAIDecideOptions = {
	getCustomAIInputConfig?: GetCustomAIInputConfig;
	supportedInputTypes?: string[];
	complexTypes?: string[];
	disableRpc?: boolean;
};

type PrivateLetAIDecideOptions = {
	aiInputConfig: AIInputConfig;
};

function getLetAIDecideTemplate({
	aiInputConfig,
	extraInstructions,
	letAiAgentDecideChecked,
	onLetAiDecideChange,
	onExtraInstructionsChange,
}: PrivateLetAIDecideOptions & {
	extraInstructions: string;
	letAiAgentDecideChecked: boolean;
	onLetAiDecideChange: (checked: boolean) => void;
	onExtraInstructionsChange: (value: string) => void;
}) {
	const allowExtraInstructions = Features.isOn('ai-extra-instructions');
	const html = `
		<div class="${classes['ai-pill']} form-control">
			<div class="dmo-flex dmo-justify-between dmo-items-center dmo-mt-4">
				<span class="${classes['ai-pill-content']}">
					<i class="far fa-wand-magic-sparkles"></i> ${aiInputConfig.label}
				</span>
				${
					allowExtraInstructions
						? `
					<span class="${classes['ai-pill-extra-instructions-btn']}" role="button">
						<i class="far fa-pencil"></i> ${I18n.l('aiBlock.addDetails')}
					</span>`
						: ''
				}
			</div>

			<div class="${classes['ai-pill-extra-instructions-text']}">
				<input name="extra-instructions" type="text" maxlength="128" placeholder="${I18n.l(
					'aiBlock.addExtraInstructions',
				)}"></input>
				<span role="button">
					<i class="far fa-times"></i>
				</span>
			</div>
		</div>
        <div class="${classes.wrapper}">
            <label>
				<input type="checkbox" name="ai-agent-decide" />
				${I18n.l('scenarioList.letAiAgentDecide')}
			</label>
        </div>
    `;

	const template = document.createElement('template');
	template.insertAdjacentHTML('beforeend', html);

	const letAiAgentDecideInput = template.querySelector('input[type="checkbox"]') as HTMLInputElement;
	letAiAgentDecideInput.checked = letAiAgentDecideChecked;

	const ai = template.querySelector<HTMLElement>(`.${classes['ai-pill']}`)!;
	ai.style.backgroundColor = aiInputConfig.backgroundColor;
	ai.style.borderColor = aiInputConfig.backgroundColor;
	ai.style.color = aiInputConfig.color;

	if (!letAiAgentDecideChecked) {
		ai.classList.add(classes['ai-pill--hidden']);
	}

	letAiAgentDecideInput.addEventListener('change', (e) => {
		const checked = (e.target as HTMLInputElement).checked;
		onLetAiDecideChange(checked);

		ai.classList.toggle(classes['ai-pill--hidden'], !checked);
	});

	const extraInstructionBtn = template.querySelector<HTMLElement>(`.${classes['ai-pill-extra-instructions-btn']}`)!;
	const closeExtraInstructionBtn = template.querySelector<HTMLElement>(
		`.${classes['ai-pill-extra-instructions-text']} span`,
	)!;
	const extraInstructionsInput = template.querySelector<HTMLInputElement>(
		`.${classes['ai-pill-extra-instructions-text']} input`,
	)!;

	if (allowExtraInstructions) {
		extraInstructionBtn.addEventListener('click', () => {
			ai.classList.add(classes['ai-pill-extra-instructions']);
			extraInstructionsInput?.focus();
		});

		closeExtraInstructionBtn.addEventListener('click', () => {
			ai.classList.remove(classes['ai-pill-extra-instructions']);
			(extraInstructionsInput as HTMLInputElement).value = '';
			onExtraInstructionsChange('');
		});

		if (extraInstructions) {
			ai.classList.add(classes['ai-pill-extra-instructions']);
			(extraInstructionsInput as HTMLInputElement).value = extraInstructions;
		}

		extraInstructionsInput.addEventListener('change', (e) => {
			const input = e.target as HTMLInputElement;
			onExtraInstructionsChange(input.value);
		});
	}

	return template;
}

function addToExtraState(field: FieldLike, value: { [key: string]: unknown }) {
	field._extraState = {
		...field._extraState,
		...value,
	};

	return field;
}

function removeFromExtraState(field: FieldLike, key: string) {
	if (field._extraState) {
		delete field._extraState[key];
	}

	return field;
}

// instructions help is used for AI Agent to better understand the field's purpose and requirements.
function addHelpToExtraState(field: FieldLike) {
	if (field._instructions?.help) {
		addToExtraState(field, { aiHelp: field._instructions.help });
	}
}

export function renderLetAIDecide(field: FieldLike, { aiInputConfig }: PrivateLetAIDecideOptions) {
	let backupValue = field.value;
	let backupMode = field.mode;
	let letAiAgentDecideChecked = false;

	const checkByDefault = field.required && (field.value === '' || field.value === null || field.value === undefined);

	if (field.value === aiInputConfig.name) {
		letAiAgentDecideChecked = true;
		backupValue = '';
		backupMode = 'map';
	} else if (checkByDefault) {
		letAiAgentDecideChecked = true;

		if (field.hasModeSwitch) {
			field.mode = 'map';
		}

		field.value = aiInputConfig.name;
	}

	if (letAiAgentDecideChecked || checkByDefault) {
		addHelpToExtraState(field);
	}

	const onLetAiDecideChange = (checked: boolean) => {
		if (checked) {
			backupValue = field.value;
			backupMode = field.mode;

			updateInputField(true, 'map');
			addHelpToExtraState(field);
			field.value = aiInputConfig.name;
		} else {
			updateInputField(false, backupMode);
			removeFromExtraState(field, 'aiHelp');
			field.value = backupValue;
		}

		field.validate();
		field.setValueToFormValues();
	};

	const onExtraInstructionsChange = (value: string) => {
		if (value) {
			addToExtraState(field, {
				aiInstruction: value,
			});
		} else if (field?._extraState?.aiInstruction) {
			removeFromExtraState(field, 'aiInstruction');
		}
	};

	const extraInstructions = (field?._extraState?.aiInstruction as string) || '';
	const template = getLetAIDecideTemplate({
		aiInputConfig,
		extraInstructions,
		letAiAgentDecideChecked,
		onLetAiDecideChange,
		onExtraInstructionsChange,
	});

	type ImtSwitch = HTMLElement & { disabled: boolean };

	const updateInputField = (isLetAiDecideEnabled: boolean, mode: FieldLike['mode']) => {
		const fieldModeSwitch = field.hasModeSwitch && field.querySelector<ImtSwitch>('imt-switch');

		if (fieldModeSwitch) {
			fieldModeSwitch.disabled = false;
			field.mode = mode;
			fieldModeSwitch.disabled = isLetAiDecideEnabled;
		}

		if (CUSTOM_MAPPABLE_INPUT_TYPES.includes(field.inputType)) {
			field.mode = mode;
		}

		// Hide the input field coder/control or wrapper (f.e. filter)
		const controlField = field._coder || field._control || field._wrapper;
		if (controlField) {
			// Check if the coder input is directly inside an input group or not
			const targetElement = controlField.parentElement?.classList.contains('input-group')
				? controlField.parentElement
				: controlField;

			targetElement.style.display = isLetAiDecideEnabled ? 'none' : '';
		}
	};

	updateInputField(letAiAgentDecideChecked, checkByDefault ? 'map' : backupMode);
	field.validate();
	field._validationMessages.before(...template.children);
}

export function addLetAIDecide(
	field: FieldLike,
	{ getCustomAIInputConfig, supportedInputTypes, complexTypes, disableRpc }: LetAIDecideOptions = {},
) {
	if (field.domain === 'aiagent') {
		return;
	}
	const supportedTypes = supportedInputTypes || SUPPORTED_INPUT_TYPES;
	const complexInputTypes = complexTypes || COMPLEX_INPUT_TYPES;
	if (!supportedTypes.includes(field.inputType)) {
		return;
	}

	const isMappable = field.isCoder?.() || field.hasModeSwitch;
	const isCustomMappable = CUSTOM_MAPPABLE_INPUT_TYPES.includes(field.inputType);

	if (disableRpc && field.hasRpc && field.inputType !== 'text') {
		return;
	}

	if (!isMappable && !isCustomMappable) {
		return;
	}

	if (field.parentField && field.parentField.closest('imt-input-array, imt-input-udtspec')) {
		return;
	}

	if (field.hasRpc && !field.hasModeSwitch && !field.isCoder?.()) {
		return;
	}

	if (
		hasUnsupportedFieldInstructions(field._instructions, {
			strictCheck: complexInputTypes.includes(field.inputType),
			depth: 0,
			supportedTypes,
		})
	) {
		return;
	}

	const sanitizePath = sanitizePathForVariables(field.path);

	const aiInputConfig: AIInputConfig = {
		...getAIInput(sanitizePath),
		...getCustomAIInputConfig?.({ path: sanitizePath, ...field.configSnapshot }),
	};

	if (CUSTOM_MAPPABLE_INPUT_TYPES.includes(field.inputType)) {
		// Handle custom mappable input types
		if (field.inputType === 'filter') {
			addLetAIDecideToFilter(field, aiInputConfig);
			return;
		}
	}

	renderLetAIDecide(field, {
		aiInputConfig,
	});
}

function addLetAIDecideToFilter(field: FieldLike, aiInputConfig: AIInputConfig) {
	const imlValue = aiInputConfig?.name?.replace(/{{|}}/g, '');

	const syncWithField = () => {
		const aiInstruction: string[] = [];
		field
			.querySelectorAll('div.item:has(input[name="ai-agent-decide"]:checked) > imt-coder')
			.forEach((coder: any, index: number) => {
				coder.value = `{{${imlValue}[${index}]}}`;
				aiInstruction.push(coder?.parentElement?.querySelector('input[name="extra-instructions"]')?.value || '');
			});

		addToExtraState(field, { aiInstruction });
	};

	const addLetAIDecide = (coder: Coder) => {
		const coderValue: string = coder.value;
		// Check if the coder value matches the pattern {{imlValue[index]}}
		const matchPattern = coderValue.match(/{{.+\[(\d+)\]}}/);
		const index = matchPattern ? Number(matchPattern[1]) : -1;

		const hasLetAIDecide = index > -1 && coder.value === `{{${imlValue}[${index}]}}`;

		if (hasLetAIDecide) {
			coder.style.display = 'none';
		}
		let backupValue = !hasLetAIDecide ? coder.value : '';
		const template = getLetAIDecideTemplate({
			aiInputConfig,
			extraInstructions:
				index > -1 && Array.isArray(field?._extraState?.aiInstruction) ? field._extraState.aiInstruction[index] : '',
			letAiAgentDecideChecked: hasLetAIDecide,
			onLetAiDecideChange: (checked) => {
				(coder as HTMLElement).style.display = checked ? 'none' : '';
				if (!checked) {
					coder.value = backupValue;
				} else {
					backupValue = coder.value;
				}

				syncWithField();
			},
			onExtraInstructionsChange: () => {
				syncWithField();
			},
		});

		coder.after(...template.children);
	};

	EventManager.addListener(field, EVENTS.FILTERS.BUILT, (e: CustomEvent<HTMLElement>) => {
		const item = e.detail;
		const coder = item?.querySelector<Coder>('imt-coder');
		if (coder) {
			addLetAIDecide(coder);
		}
	});

	const values = field.querySelectorAll<Coder>('div.item > imt-coder');

	values.forEach((coder) => {
		addLetAIDecide(coder);
	});
}

export function getLetAIDecideInputConfig(form: FormanLike) {
	const inputsConfig: FormanConfigLike[] = [];
	const parentByPath = new Map();

	const checkedFields = Array.from(form.querySelectorAll('input[name="ai-agent-decide"]:checked')).filter(
		(x) => !x.closest('imt-fieldset[hidden]'),
	);

	for (const checked of checkedFields) {
		const field = checked.closest<FieldLike>('[field]');

		if (!field) {
			continue;
		}

		const pathSegments = field.path.split('.');

		if (pathSegments.length <= 1) {
			inputsConfig.push(field.configSnapshot);
			continue;
		}

		let current: FieldLike | null = field;

		while (pathSegments.length > 1) {
			const currentPath = pathSegments.pop()!;
			const parentPath = pathSegments.join('.');

			if (!parentByPath.has(parentPath)) {
				const parentField = current?.parentField?.closest<FieldLike>('imt-input-collection, [type="array-item"]');
				const parentConfig = {
					...parentField?.configSnapshot,
					spec: [],
				};
				parentByPath.set(parentPath, parentConfig);

				if (pathSegments.length === 1) {
					inputsConfig.push(parentConfig as FormanConfigLike);
				}
			}

			const parent = parentByPath.get(parentPath);
			const fullCurrentPath = pathSegments.concat(currentPath).join('.');
			const currentSpec = parentByPath.get(fullCurrentPath) || current?.configSnapshot;

			if (!parent.spec.includes(currentSpec)) {
				parent.spec.push(currentSpec);
			}

			current = current?.parentField?.closest('imt-input-collection, [type="array-item"]') || null;
		}
	}

	return sanitizeInputConfigArray(inputsConfig);
}

declare global {
	interface HTMLElementEventMap {
		change: CustomEvent<{ value: string }>;
	}
}
